import React, { useState, useEffect, useMemo, useRef } from "react";

import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";

import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { getEncoding, encodingForModel } from "js-tiktoken";

import { SSE } from "sse";

const TOKEN_LIMITS = {
  "gpt-4": 8000,
  "gpt-4-turbo-preview": 120000,
  "gpt-3.5-turbo": 4050,
};

const filmIdeaPrompt1 = "List 5 ideas for a short film about ";
const filmIdeaPrompt2 =
  ". Include character development and explain why the film is different from the tropes of the genre.";

const filmIdeas = [
  "dragons and dwarfs: mountain siege",
  "mars colony: rebel uprising",
  "love letters across time",
  "haunted lighthouse: island mystery",
  "mismatched roommates: hilarious chaos",
  "heist at the cyberbank",
  "family secrets: ancestral home",
  "whispering shadows: unsolved crimes",
  "jungle quest for lost city",
  "revolution: the untold story",
  "time loop in ancient rome",
  "virtual world escape",
  "undercover in 1920s paris",
  "alien artifact discovery",
  "pirates of the forgotten seas",
  "cyberpunk city rebellion",
  "haunted victorian mansion",
  "wild west robot showdown",
  "post-apocalyptic road journey",
  "deep sea alien encounter",
  "medieval kingdom betrayal",
  "lost in a digital universe",
  "celebrity life undercover",
  "arctic expedition survival",
  "ancient egyptian curse",
  "parallel lives intersect",
  "desert oasis mirage",
  "robotic uprising in 2077",
  "summer camp secrets",
  "moon colony conspiracy",
];

function Gpt(props) {
  const [messages, setMessages] = React.useState([]);
  const [hasStarted, setHasStarted] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [promptNum, setPromptNum] = React.useState(0);
  const [lastPromptRun, setLastPromptRun] = React.useState(-1);
  const [showAfter, setShowAfter] = React.useState(-1);
  const [showScroll, setShowScroll] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [usedPrompts, setUsedPrompts] = React.useState(new Set());

  const prompts = [
    {
      title: "Film idea",
      helpText: "",
      fillIn: "FILL IN HERE",
      promptVal: [
        "List 5 ideas for a short film about ",
        ". Include character development and explain why the film is different from the tropes of the genre. Use a style that grabs and holds the attention of readers.",
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "10px 15px 15px 15px",
                borderRadius: "15px",
                border: "2px solid #f05f40",
                boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
              }}
            >
              <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                Instructions:
              </h3>
              <p style={{ margin: "0" }}>
                Choose the film title you like the most and select{" "}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePromptButtonClicked(null, 1)}
                  style={{
                    margin: "0 5px",
                    padding: "4px 12px",
                    fontSize: "14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.3s ease"
                  }}
                >
                  2: Script Outline
                </Button>{" "}
                to further develop your film.
              <br />
              <br />
              Don't see a title that fits? No problem! Instead adjust the
              current prompt to help the AI better understand your film and
              generate the perfect film idea.
              </p>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Script outline",
      fillIn: "FILL IN YOUR FAVORITE TITLE FROM FILM IDEA",
      promptVal: [
        "Movie title: ",
        "<br>Make a detailed outline for 5 scenes in a short film. Include a list of characters and a list of scenes.",
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "10px 15px 15px 15px",
                borderRadius: "15px",
                border: "2px solid #f05f40",
                boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
              }}
            >
              <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                Instructions:
              </h3>
              <p style={{ margin: "0" }}>
                AI tools like text generation work best with specific information.
                Let's start by generating the script for Scene 1. Once that's
                complete, you can move on to generate the rest of your scenes.
                <br />
                <br />
                Click{" "}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePromptButtonClicked(null, 2)}
                  style={{
                    margin: "0 5px",
                    padding: "4px 12px",
                    fontSize: "14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.3s ease"
                  }}
                >
                  3: Generate a script
                </Button>{" "}
                to begin writing your scene.
              </p>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Generate a script",
      fillIn: "1",
      promptVal: [
        "Write a complete script for Scene ",
        ".  Include everything needed to film the scene, such as prompts, description of the set, and a complete script with dialogue.",
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "10px 15px 15px 15px",
                borderRadius: "15px",
                border: "2px solid #f05f40",
                boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
              }}
            >
              <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                Instructions:
              </h3>
              <p style={{ margin: "0" }}>
                Now let's create a character{" "}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePromptButtonClicked(null, voiceOverPromptNumber)}
                  style={{
                    margin: "0 5px",
                    padding: "4px 12px",
                    fontSize: "14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.3s ease"
                  }}
                >
                  4. Voice Over
                </Button>{" "}
                for the movie trailer.
              </p>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Voice Over",
      fillIn: "",
      promptVal: [
        "Using the scene outline, craft a 50-word, present-tense voice-over monologue for the leading protagonist's movie-trailer intro. Reveal their quirks, introduce the core theme, hint at the stakes, and set the tone. Output only the voice-over text, no stage directions or extra lines.",
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "10px 15px 15px 15px",
                borderRadius: "15px",
                border: "2px solid #f05f40",
                boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
              }}
            >
              <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                Instructions:
              </h3>
              <p style={{ margin: "0" }}>
                Next, let's create a short description of your film called an{" "}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePromptButtonClicked(null, elevatorPitchPromptNumber)}
                  style={{
                    margin: "0 5px",
                    padding: "4px 12px",
                    fontSize: "14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.3s ease"
                  }}
                >
                  5. Elevator Pitch
                </Button>{" "}
                . Be prepared to present this text to the class.
              </p>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Elevator pitch",
      fillIn: "«FILL IN HERE»",
      promptVal: [
        "Describe a 30 word elevator pitch for the film. Include the climax, rising and falling actions. Write in the style of a film trailer.",
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "10px 15px 15px 15px",
                borderRadius: "15px",
                border: "2px solid #f05f40",
                boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
              }}
            >
              <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                Instructions:
              </h3>
              <p style={{ margin: "0" }}>
                Your production elements are coming to life. Now, let's generate descriptions for your{" "}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePromptButtonClicked(null, prompts.findIndex(prompt => prompt.title === "Movie poster"))}
                  style={{
                    margin: "0 5px",
                    padding: "4px 12px",
                    fontSize: "14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.3s ease"
                  }}
                >
                  6. Movie Poster
                </Button>{" "}
                — these will be used to create images.
              </p>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Movie poster",
      fillIn: "«FILL IN HERE»",
      promptVal: [
        "Describe a film poster in exquisite detail for this movie. Include the style of the art. Generate 4 options.",
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "10px 15px 15px 15px",
                borderRadius: "15px",
                border: "2px solid #f05f40",
                boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)",
              }}
            >
              <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                Instructions:
              </h3>
              <p style={{ margin: "0" }}>
                Now generate your movie poster image!
                <br />
                <br />
                Select one option above and copy/paste in{" "}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => props.setActiveTab("dalle")}
                  style={{
                    margin: "0 5px",
                    padding: "4px 12px",
                    fontSize: "14px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                    borderColor: "#f05f40",
                    color: "#f05f40"
                  }}
                >
                  Image Generation
                </Button>
              </p>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Marketing materials",
      fillIn: "«FILL IN HERE»",
      promptVal: [
        "Make me a list of marketing materials I should create for the film.",
      ],
      showAfter: <></>,
    },
    {
      title: "Music",
      fillIn: "1",
      promptVal: [
        "Describe music for scene ",
        " in a few sentences.  Be detailed about which instruments and styles are present.",
      ],
      showAfter: <></>,
    },
    {
      title: "Production budget",
      fillIn: "«FILL IN HERE»",
      promptVal: [
        "I want you to create a complete budget for the filming. Produce that budget step-by-step, include estimated prices, and include links for where I can buy things.",
      ],
      showAfter: <></>,
    },
    {
      title: "Convert budget to spreadsheet",
      fillIn: "1",
      promptVal: [
        "Reproduce that output in a summary form as .csv text. Include the description as another column.",
      ],
      showAfter: <></>,
    },
    {
      title: "Insurance",
      fillIn:
        "It's time for YOU to write your own prompt!  Ask GPT about what insurance you'll need for the film.",
      promptVal: ["", ""],
      showAfter: <></>,
    },
    {
      title: "List of equipment to rent",
      fillIn:
        "It's time for YOU to write your own prompt!  Ask GPT about what you'll need for the film.",
      promptVal: ["", ""],
      showAfter: <></>,
    },
    {
      title: "Casting call",
      fillIn:
        "It's time for YOU to write your own prompt!  Ask GPT to help you make a casting call for the film.",
      promptVal: ["", ""],
      showAfter: <></>,
    },
    {
      title: "Day-of shooting schedule",
      fillIn:
        "It's time for YOU to write your own prompt!  Ask GPT to make a day-of shooting schedule.",
      promptVal: ["", ""],
      showAfter: <></>,
    },
    {
      title: "Social media post",
      fillIn:
        "It's time for YOU to write your own prompt!  Ask GPT to help you with some social media posts.",
      promptVal: ["", ""],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "15px",
                borderRadius: "15px",
              }}
            >
              Your film needs a launch website. Let's ask GPT-4 to program a
              html-only website.
            </p>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Website",
      fillIn: "FILL IN",
      promptVal: [
        "Movie Title: ",
        `<br />
      Description: FILL IN<br />
      Starring: FILL IN<br />
      Write a simple HTML landing page website for launching the movie.  Use nice formatting, text colors, and fonts.  Make a spot for us to put the movie poster as a .png, 600 pixels wide.  Also add more content subsections and links to other parts of the website as well as external links (like where can I buy tickets).`,
      ],
      showAfter: (
        <>
          <hr />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                fontSize: "large",
                padding: "15px",
                borderRadius: "15px",
              }}
            >
              <ul>
                <li>Copy the .html code → paste code into notepad</li>

                <li>Save notepad on desktop as: MOVIE_TITLE_website.html</li>

                <li>
                  Double-click on the saved file to view it locally in your
                  browser.
                </li>

                <li>
                  You may need to rename your movie poster .png so it can show
                  up in the website.
                </li>

                <li>HTML can only render a "static" website. </li>
                <li>
                  One way to make it dynamic is by adding Javascript, by just
                  asking GPT:
                </li>
                <code>
                  Can you add Javascript code so that when you hover over the
                  movie poster, it turns black and white?
                </code>
                <li>What other website features can you add?</li>
              </ul>
            </div>
          </div>
          <hr />
        </>
      ),
    },
    {
      title: "Poem",
      fillIn: "",
      promptVal: [
        "Write a 12-line rhyming poem that summarizes the plot of the film. Add emojis throughout the poem to enhance the storytelling.",
      ],
      showAfter: <></>,
    },
  ];

  // Get the number of the voice over prompt
  const voiceOverPromptNumber = prompts.findIndex(prompt => prompt.title === "Voice Over");

  const elevatorPitchPromptNumber = prompts.findIndex(prompt => prompt.title === "Elevator pitch");

  const checkScrollTop = () => {
    const scrollPosition = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Show scroll to top button when scrolled down
    if (!showScroll && scrollPosition > 400) {
      setShowScroll(true);
    } else if (showScroll && scrollPosition <= 400) {
      setShowScroll(false);
    }
    
    // Show scroll to bottom button when not at the bottom
    // Hide it when within 400px of the bottom
    if (!showScrollBottom && scrollPosition + windowHeight < documentHeight - 400) {
      setShowScrollBottom(true);
    } else if (showScrollBottom && scrollPosition + windowHeight >= documentHeight - 400) {
      setShowScrollBottom(false);
    }
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    window.addEventListener("scroll", checkScrollTop);
    // return () => window.removeEventListener("scroll", checkScrollTop);
  }, []);

  useEffect(() => {
    if (props.activeTab === "gpt") {
      if (messages?.length < 1) {
        handlePromptButtonClicked(null, 0);

        if (!hasStarted) {
          const savedMessages = localStorage.getItem("gpt-messages");

          if (savedMessages) {
            try {
              let parsed = JSON.parse(savedMessages);
              if (parsed) {
                console.log(parsed);
                setMessages(parsed);
                setHasStarted(true);
              }
            } catch {
              console.log("Failed to parse saved messages.");
            }
          }
        }
      } else {
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "instant",
          });
        }, 50);
      }
    }
  }, [props.activeTab]);

  const requestId = React.useRef(0);
  const questionTextRef = useRef(null);

  let currentStreamId = useRef(null);
  let ignoreStreamId = useRef(null);

  let spinnerDisp = "";

  let mainSummary = <></>;
  if (messages !== undefined && messages.length > 0) {
    mainSummary = <MessagesDisplay messages={messages} setVoiceOver={props.setVoiceOver} setElevatorPitch={props.setElevatorPitch} />;
    spinnerDisp = "none";
  }

  let chatBoxTopMargin = hasStarted ? "20px" : "";

  let respDisp = hasStarted ? "" : "none";
  const stopVis = isStreaming ? "visible" : "hidden";

  let gptName = "GPT4";
  if (props.gptModel.includes("gpt-3.5")) {
    gptName = "GPT3.5";
  }

  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header
              as="h4"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              Chat with {gptName}
              {messages?.length > 1 && (
                <>
                  <Button variant="outline-secondary" onClick={reset}>
                    Clear all messages
                  </Button>
                </>
              )}
            </Card.Header>

            <Card.Body>
              <div style={{
                backgroundColor: "white",
                border: "2px solid #f05f40",
                borderRadius: "8px",
                padding: "10px 15px 15px 15px",
                margin: "10px 0",
                boxShadow: "0 0 10px rgba(240, 95, 64, 0.3)"
              }}>
                <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>
                  Instructions:
                </h3>
                <h4 style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0 0 5px 0" }}>
                  Follow the prompt templates to bring your film to life with AI.
                </h4>
                <p style={{ color: "#f05f40ff", fontWeight: "bold", margin: "0" }}>
                  Remember: apply prompt engineering to enhance and personalize
                  the templates.
                </p>
              </div>

              <section
                className="response-section"
                style={{ display: respDisp }}
              >
                {mainSummary}
              </section>
              <section
                className="input-section"
                style={{ marginTop: chatBoxTopMargin }}
              >
                <Form
                  onSubmit={(e) => {
                    onSubmit(e);
                  }}
                >
                  <div style={{ margin: "10px", marginBottom: "30px" }}>
                    {generatePromptButtons()}
                    {generateHelpText()}
                  </div>
                  <hr style={{ marginTop: "40px" }} />
                  <div style={{ position: "relative" }}>
                    <div style={{ 
                      position: "absolute", 
                      top: "8px", 
                      left: "50px", 
                      color: "#999", 
                      fontWeight: "bold", 
                      fontSize: "12px",
                      fontStyle: "italic",
                      zIndex: 10,
                      pointerEvents: "none"
                    }}>
                      Prompt for {gptName}:
                    </div>
                    <div
                      onInput={checkIfEmpty}
                      className="form-control orange-highlight"
                      contentEditable={!isStreaming}
                      ref={questionTextRef}
                      data-placeholder={
                        "Type your message to " + gptName + "..."
                      }
                      onKeyDown={checkForCtrlEnter}
                      style={{ minHeight: "6.5em", position: "relative", width: "100%" }}
                    >
                    </div>
                    <Button
                      variant="primary"
                      type="submit"
                      className="btn-gpt-go"
                      disabled={isStreaming}
                    >
                      Go
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-gpt-stop"
                      style={{
                        visibility: stopVis,
                      }}
                      onClick={handleStopButton}
                    >
                      Stop
                    </Button>
                  </div>
                </Form>

                {messages?.length > 1 && (
                  <>
                    <br />
                    <Button
                      variant="outline-secondary"
                      style={{ marginTop: "20px" }}
                      onClick={reset}
                    >
                      Clear all messages
                    </Button>
                  </>
                )}
              </section>
            </Card.Body>
          </Card>
          <Button
            onClick={scrollTop}
            style={{
              display: showScroll ? "flex" : "none",
              position: "fixed",
              bottom: "80px",
              right: "20px",
              zIndex: "1000",
            }}
            variant="secondary"
          >
            ↑
          </Button>
          <Button
            onClick={scrollBottom}
            style={{
              display: showScrollBottom ? "flex" : "none",
              position: "fixed",
              bottom: "20px",
              right: "20px",
              zIndex: "1000",
            }}
            variant="secondary"
          >
            ↓
          </Button>
        </Col>
      </Row>
    </Container>
  );

  function generateHelpText() {
    if (prompts[promptNum] && "helpText in prompts[promptNum]") {
      return <>{prompts[promptNum]["helpText"]}</>;
    }
    return <></>;
  }

  function generatePromptButtons() {
    const baseButtonStyle = { 
      marginRight: "10px", 
      marginBottom: "0px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      transition: "all 0.3s ease"
    };
    
    const usedButtonStyle = {
      ...baseButtonStyle,
      backgroundColor: "#f0f8ff",
      borderColor: "#6c757d",
      color: "#6c757d"
    };
    
    // Split buttons into two groups: 1-6 (always shown) and 7-17 (conditional)
    let primaryButtons = [];
    let advancedButtons = [];
    
    for (let i = 0; i < prompts.length; i++) {
      const isUsed = usedPrompts.has(i);
      let variant = "outline-secondary";
      let btnClass = "";
      let buttonStyle = isUsed ? { ...usedButtonStyle } : { ...baseButtonStyle };
      
      if (promptNum == i) {
        variant = "outline-primary";
        buttonStyle = {
          ...baseButtonStyle,
          fontSize: "1.2em",
          fontWeight: "bold",
          border: "2px solid #007bff",
          boxShadow: "0 0 5px rgba(0, 123, 255, 0.3)"
        };
      }
      
      if (lastPromptRun + 1 == i && !isStreaming) {
        btnClass = "animated-outline";
      }
      
      const button = (
        <Button
          variant={variant}
          style={buttonStyle}
          key={i}
          onClick={(e) => handlePromptButtonClicked(e, i)}
          className={btnClass}
        >
          {i < 6 ? `${i + 1}. ` : ''}{prompts[i]["title"]}
        </Button>
      );
      
      // Buttons 1-6 (indices 0-5) go to primaryButtons
      // Buttons 7-17 (indices 6-16) go to advancedButtons
      if (i < 6) {
        primaryButtons.push(button);
      } else {
        advancedButtons.push(button);
      }
    }
    
    return (
      <div>
        {/* Always show buttons 1-6 */}
        <div style={{
          display: "flex",
          overflowX: "auto",
          paddingBottom: "10px",
          gap: "10px",
          scrollbarWidth: "thin",
          scrollbarColor: "#ccc #f1f1f1",
          alignItems: "center"
        }}>
          {primaryButtons}
        </div>
        
        {/* Only show buttons 7-17 after image generation starts */}
        {props.imageGenerationStarted && (
          <>
            <hr style={{
              border: "none",
              borderTop: "1px solid #d3d3d3",
              margin: "5px 0 10px 0"
            }} />
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              paddingBottom: "10px",
              gap: "10px",
              alignItems: "center"
            }}>
              {advancedButtons}
            </div>
          </>
        )}
      </div>
    );
  }

  function handlePromptButtonClicked(e, index) {
    if (index < 0 || index >= prompts.length) {
      console.error("Invalid index");
      return;
    }

    setPromptNum(index);

    let htmlContent = "";
    let fillInPosition;

    const randomNumber = Math.floor(Math.random() * filmIdeas.length);

  let fillInStr = prompts[index]["fillIn"];
  if (index == 0) {
    // For the first prompt (Film idea), use just the example without "FILL IN HERE"
    fillInStr = filmIdeas[randomNumber];
  }
    // fillInStr='<span style="font-size:120%; color: #f05f40">' + fillInStr + '</span>';

    const values = prompts[index]["promptVal"];
    if (values.length === 2) {
      // Store the start position of "FILL IN"
      fillInPosition = values[0].length;
      // Insert "FILL IN" between the two values
      htmlContent = `${values[0]}${fillInStr}${values[1]}`;
    } else {
      // Single value case
      htmlContent = values[0];
    }

    questionTextRef.current.innerHTML = htmlContent;

    // Automatically highlight "FILL IN" if it exists
    if (fillInPosition !== undefined) {
      highlightText(fillInPosition, fillInStr.length);
    }

    // questionTextRef.current.innerHTML = `${filmIdeaPrompt1}<strong>${e.target.value}</strong>${filmIdeaPrompt2}`
    // setFilmIdeaPrompt()
  }

  function highlightText(startPosition, length) {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStart(questionTextRef.current.firstChild, startPosition);
    range.setEnd(questionTextRef.current.firstChild, startPosition + length);

    selection.removeAllRanges();
    selection.addRange(range);
  }

  // function highlightText(startPosition, length) {
  //   const range = document.createRange();
  //   const selection = window.getSelection();
  //   const node = questionTextRef.current.childNodes[0]; // Assuming the first child is the text node, this might need adjustment

  //   try {
  //     range.setStart(node, startPosition);
  //     range.setEnd(node, startPosition + length);
  //     selection.removeAllRanges();
  //     selection.addRange(range);
  //   } catch (error) {
  //     console.error('Error highlighting text:', error);
  //   }
  // }

  function saveMesssagesToLocalStorage(newMessages) {
    try {
      localStorage.setItem("gpt-messages", JSON.stringify(newMessages));
    } catch (e) {
      console.error("An error occurred while saving to Local Storage", e);
    }
  }

  function checkIfEmpty() {
    if (questionTextRef.current.innerHTML === "<br>") {
      questionTextRef.current.innerHTML = "";
    }
  }

  function checkForCtrlEnter(e) {
    if (!e.ctrlKey && !e.shiftKey && e.code == "Enter") {
      onSubmit(e);
    }
  }

  function reset() {
    setHasStarted(false);
    requestId.current += 1;
    setMessages([]);
    setUsedPrompts(new Set());
    try {
      localStorage.setItem("gpt-messages", JSON.stringify([]));
    } catch (e) {
      console.error("Failed to save messages to localStorage:", e);
    }
    questionTextRef.current.focus();
  }

  function MessagesDisplay(props) {
    let counter = 0;
    let out = [];
    let nextIsVoiceOver = false;
    let nextIsElevatorPitch = false;
    
    // Show spinner if no messages exist
    if (props.messages.length === 0) {
      return (
        <div style={{
          padding: "15px",
          backgroundColor: "#ebebeb",
          borderRadius: "5px"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px"
          }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ marginLeft: "10px", color: "#666" }}>Generating response...</span>
          </div>
        </div>
      );
    }
    
    for (let message of props.messages) {
      out.push(<SingleMessageDisplay message={message} key={counter} couldBeVoiceOver={nextIsVoiceOver} setVoiceOver={props.setVoiceOver} setElevatorPitch={props.setElevatorPitch} couldBeElevatorPitch={nextIsElevatorPitch} />);

      if (message["role"] === "user" && message["content"].includes("voice-over monologue")) {
        nextIsVoiceOver = true;
      } else {
        nextIsVoiceOver = false;
      }

      if (message["role"] === "user" && message["content"].includes("elevator pitch for the film")) {
        nextIsElevatorPitch = true;
      } else {
        nextIsElevatorPitch = false;
      }
      counter += 1;
    }

    return (
      <div>
        <div>{out}</div>
      </div>
    );
  }

  function limitMessagesByTokenCount(messages, tokenLimit) {
    const enc = encodingForModel("gpt-4");

    let totalTokens = 0;
    let processed = [];

    for (let i = messages.length - 1; i >= 0; i--) {
      const tokens = enc.encode(messages[i]["content"]);
      if (totalTokens + tokens.length > tokenLimit) {
        break; // Exceeds token limit, so stop adding more messages
      }
      totalTokens += tokens.length;
      processed.unshift(messages[i]); // Add to the start of the processed array
    }

    console.log(totalTokens);

    return processed;
  }

  function SingleMessageDisplay(props) {
    const bgColor =
      props.message["role"] === "assistant" ? "#ebebeb" : "#007bff";
    const fgColor =
      props.message["role"] === "assistant" ? "#000000" : "white";
    const textSize = props.message["role"] === "assistant" ? "normal" : "large";
    const fontWeight = props.message["role"] === "user" ? "bold" : "normal";
    const borderStyle = props.message["role"] === "user" ? "2px solid #007bff" : "none";

    // Create a ref to the message content.
    const messageRef = useRef(null);

    // State to handle button's label.
    const [buttonLabel, setButtonLabel] = useState("Copy");

    // Function to copy the message content to the clipboard.
    const copyToClipboard = async () => {
      if (messageRef && messageRef.current) {
        try {
          // Use Clipboard API to copy text
          if (typeof ClipboardItem !== "undefined") {
            const html = new Blob([messageRef.current.innerHTML], {
              type: "text/html",
            });
            const text = new Blob([props.message["content"]], {
              type: "text/plain",
            });
            const data = new ClipboardItem({
              "text/html": html,
              "text/plain": text,
            });
            await navigator.clipboard.write([data]);
          } else {
            // Fallback using the deprecated `document.execCommand`.
            // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand#browser_compatibility
            const cb = (e) => {
              e.clipboardData.setData(
                "text/html",
                messageRef.current.innerHTML
              );
              e.clipboardData.setData("text/plain", props.message["content"]);
              e.preventDefault();
            };
            document.addEventListener("copy", cb);
            document.execCommand("copy");
            document.removeEventListener("copy", cb);
          }

          // await navigator.clipboard.writeText(messageRef.current.innerHTML);
          // Change button label
          setButtonLabel("Copied!");
          // Reset button label after 1 second
          setTimeout(() => {
            setButtonLabel("Copy");
          }, 1000);
        } catch (err) {
          console.error("Failed to copy text: ", err);
        }
      }
    };

    let this_message = <></>;
    if (props.message["role"] === "assistant") {
      if (props.message["content"] === "") {
        // Show spinner for empty assistant messages (during streaming)
        this_message = (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px"
          }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ marginLeft: "10px", color: "#666" }}>Generating response...</span>
          </div>
        );
      } else {
        this_message = (
          <div ref={messageRef}>
            <Markdown remarkPlugins={[remarkBreaks]}>
              {props.message["content"]}
            </Markdown>
          </div>
        );
      }
    } else {
      this_message = <span ref={messageRef}>{props.message["content"]}</span>;
    }

    let showAfterResult = <></>;
    console.log(props.message["showAfter"]);
    if (props.message["showAfter"] !== undefined && props.message["showAfter"] >= 0) {
      showAfterResult = prompts[props.message["showAfter"]].showAfter;
      console.log(showAfterResult);
    }

    // Check for the voice over, if that is it, we need to copy that into the box for the voice cloning
    if (props.message["showAfter"] === voiceOverPromptNumber || props.couldBeVoiceOver) {
      props.setVoiceOver(props.message["content"]);
    }

    if (props.message["showAfter"] === elevatorPitchPromptNumber || props.couldBeElevatorPitch) {
      props.setElevatorPitch(props.message["content"]);
    }

    return (
      <div
        style={{
          padding: "15px",
          // whiteSpace: "pre-wrap",
          backgroundColor: bgColor,
          color: fgColor,
          fontSize: textSize,
          fontWeight: fontWeight,
          border: borderStyle,
          position: "relative",
        }}
      >
        {props.message["role"] === "user" && (
          <div style={{ 
            position: "absolute", 
            top: "5px", 
            left: "10px", 
            fontWeight: "bold", 
            color: "#ddd",
            fontSize: "12px",
            fontStyle: "italic",
            zIndex: 1
          }}>
            Prompt for GPT4:
          </div>
        )}
        {props.message["role"] === "assistant" && (
          <div style={{ 
            position: "absolute", 
            top: "5px", 
            left: "10px", 
            fontWeight: "bold", 
            color: "#999",
            fontSize: "12px",
            fontStyle: "italic",
            zIndex: 1
          }}>
            GPT4 Output:
          </div>
        )}
        <div style={{ 
          position: "relative", 
          paddingBottom: props.message["role"] === "user" ? "10px" : "35px",
          paddingTop: props.message["role"] === "user" ? "15px" : "20px"
        }}>
          {this_message}
          {/* Show Copy button only for the assistant's messages */}
          {props.message["role"] === "assistant" && (
            <Button
              variant="outline-secondary"
              size="sm"
              style={{
                position: "absolute",
                bottom: "-5px",
                right: "10px",
              }}
              onClick={copyToClipboard}
            >
              {buttonLabel}
            </Button>
          )}
        </div>
        {showAfterResult}
      </div>
    );
  }

  function getTextWithLineBreaks(divRef) {
    if (!divRef.current) return "";

    // Get innerHTML and replace <div>, <p>, and <br> with newlines
    let htmlContent = divRef.current.innerHTML;

    // Replacing opening and closing div or p tags with newlines
    htmlContent = htmlContent.replace(/<div>/g, "\n").replace(/<\/div>/g, "");
    htmlContent = htmlContent.replace(/<p>/g, "\n").replace(/<\/p>/g, "");

    // Replacing br tags with newlines
    htmlContent = htmlContent.replace(/<br\s*\/?>/g, "\n");

    // Create a new DOM element and set its innerHTML to the modified htmlContent
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // Return the textContent of the temporary div, which now has the structure we need
    return tempDiv.textContent;
  }

  function handleStopButton(e) {
    // We can't actually stop streaming, so we just stop displaying new results
    ignoreStreamId.current = currentStreamId.current;
    currentStreamId.current = "STOP_NOW";
    setIsStreaming(false);
  }

  function removeShowAfter(objectsArray) {
    // Map over the array and return a new array where each object does not have the 'showAfter' field
    return objectsArray.map((obj) => {
      const { showAfter, ...rest } = obj; // Destructure to remove 'showAfter' and capture the rest
      return rest; // Return the new object without 'showAfter'
    });
  }

  function onSubmit(e) {
    e.preventDefault();

    if (isStreaming) {
      return;
    }

    setHasStarted(true);
    setIsStreaming(true);
    const currentShowAfter = promptNum;
    setShowAfter(promptNum);
    setLastPromptRun(promptNum);
    
    // Mark this prompt as used
    setUsedPrompts(prev => new Set([...prev, promptNum]));
    
    setPromptNum(-1);

    currentStreamId.current = null;

    let text = getTextWithLineBreaks(questionTextRef);
    let new_message = { role: "user", content: text };
    questionTextRef.current.innerHTML = "";

    let token_limit = 4050;
    if (props.gptModel in TOKEN_LIMITS) {
      token_limit = TOKEN_LIMITS[props.gptModel];
    }
    console.log("token limit: " + token_limit);

    const message_limited = limitMessagesByTokenCount(
      [...removeShowAfter(messages), new_message],
      token_limit
    );

    console.log(message_limited);

    let fetch_data = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + props.openaiApiKey,
      },
      payload: JSON.stringify({
        model: props.gptModel,
        stream: true,
        max_tokens: 4096,
        messages: message_limited,
      }),
      // payload: JSON.stringify(truncateWrapper({
      //   model: 'gpt-4',
      //   messages: [...messages, new_message],
      // }))
    };

    askQuestion(fetch_data, new_message, 1, currentShowAfter);
  }

  function handleStreamError(e, attempt, fetch_data, new_message) {
    console.log("handleStreamError");

    let error_out = "";

    try {
      let error = JSON.parse(e.data);
      console.log(error);
      if ("error" in error && "message" in error["error"]) {
        error_out = error["error"]["message"];
      } else {
        error_out = error;
      }
    } catch {
      error_out = "failed to parse error data" + e;
    }

    console.log(`Stream error encountered. Attempt: ${attempt}`, error_out);

    // Define the maximum number of attempts
    const maxAttempts = 5;

    // Check if the maximum attempts have been reached
    if (attempt < maxAttempts) {
      // Calculate the exponential backoff delay
      const jitter = Math.floor(Math.random() * 20);
      const delay = Math.pow(2, attempt) * 200 + jitter; // Exponential backoff formula

      console.log(`Retrying in ${delay}ms...`);

      // Retry the request after the delay
      setTimeout(() => {
        askQuestion(fetch_data, new_message, attempt + 1, showAfter);
      }, delay);
    } else {
      console.log("Maximum retry attempts reached. Aborting.");
      props.setErrorMessage(error_out);
      setIsStreaming(false);
    }
  }

  function handleStreamAbort(e) {
    console.log("stream abort");
    console.log(e);
  }

  function askQuestion(fetch_data, new_message, attempt, currentShowAfter) {
    var source = new SSE(
      "https://api.openai.com/v1/chat/completions",
      fetch_data
    );

    let newMessages = [
      ...messages,
      new_message,
      { role: "assistant", content: "", showAfter: currentShowAfter },
    ];
    console.log(newMessages);
    setMessages(newMessages);

    source.addEventListener("error", (e) =>
      handleStreamError(e, attempt, fetch_data, new_message)
    );
    source.addEventListener("abort", handleStreamAbort);

    source.addEventListener("message", function (e) {
      // Assuming we receive JSON-encoded data payloads:
      if (e.data != "[DONE]") {
        let result = JSON.parse(e.data);

        if (
          currentStreamId.current == null &&
          ignoreStreamId.current != result.id
        ) {
          currentStreamId.current = result.id;
        } else if (currentStreamId.current != result.id) {
          console.log("ignorning non-current id return!");
          return;
        }

        if (result?.choices?.[0]?.delta?.content) {
          const output = result.choices[0].delta.content;

          let newMessages2 = [...newMessages];
          newMessages2[newMessages2.length - 1]["content"] =
            newMessages2[newMessages2.length - 1]["content"] + output;
          setMessages(newMessages2);
        } else if (result && !result.choices) {
          props.setErrorMessage("Error: " + result);
          setIsStreaming(false);
        }
      } else {
        setIsStreaming(false);
        saveMesssagesToLocalStorage(newMessages);
      }
    });

    console.log("starting stream");
    source.stream();
  }
}
export { Gpt };
